/**
 * steering.js — Turn a pointer position into a movement input frame.
 *
 * Desktop players can hand movement to the mouse: the rider flies to wherever
 * the cursor is. This module is the whole of that translation, and it is
 * deliberately pure — no React, no canvas, no side effects — so the same
 * function serves the local player, the online host and the online guest.
 *
 * It returns the SAME boolean frame the keyboard produces, and that is the
 * central constraint rather than a stylistic choice. The online guest is a thin
 * client that forwards input frames and never simulates, so anything that
 * cannot be expressed as key states — a target position, a velocity, an
 * acceleration — would not survive the trip to the host. Expressed as keys,
 * pointer steering needs no new protocol and no new authority.
 */

/**
 * How many frames ahead the controller aims to arrive in. Larger values ease in
 * more gently and overshoot less; smaller values chase the cursor harder.
 */
const APPROACH_FRAMES = 7;

/**
 * Dead band on the speed match, in px/frame. Without it the controller flips
 * between accelerating and braking every frame once it is close, which reads as
 * a rider vibrating in place.
 */
const SPEED_TOLERANCE = 0.55;

/**
 * The cursor should sit at the middle of the body, but a player's y is their
 * feet, so the vertical target is pushed down by roughly half a rider.
 */
const BODY_CENTER_OFFSET = 24;

/**
 * How far above the rider the cursor must be to ask for a jump, in pixels.
 * Comfortably above head height, so drifting the cursor along the floor does
 * not make them hop.
 */
const JUMP_REACH = 52;

/**
 * Pick the input for one axis: accelerate, brake, or coast.
 *
 * The naive approach — hold "toward the cursor" until you arrive — overshoots
 * badly here. Friction sheds board speed over roughly sixty pixels, so a rider
 * at full tilt sails well past the cursor and swings back through it. Instead
 * this matches a target SPEED that tapers as the gap closes, which means the
 * controller presses the *opposite* key when the rider is travelling too fast
 * to stop in the distance that is left. That braking is what makes the rider
 * settle on the cursor rather than oscillate around it.
 *
 * @param {number} delta    Distance still to cover (target − current).
 * @param {number} velocity Current velocity on this axis.
 * @param {number} maxSpeed Fastest the rider can travel on this axis.
 * @returns {-1|0|1} press negative, coast, or press positive.
 */
function axisInput(delta, velocity, maxSpeed) {
  const desired = Math.max(-maxSpeed, Math.min(maxSpeed, delta / APPROACH_FRAMES));
  if (desired - velocity > SPEED_TOLERANCE) return 1;
  if (velocity - desired > SPEED_TOLERANCE) return -1;
  return 0;
}

/**
 * Build the movement half of an input frame that carries a player to a point.
 *
 * Only movement: kick, beam and mode switch are left to the caller, because
 * they stay on the keyboard. The primary scheme's action keys (Q, Shift, E) are
 * all left-hand keys, so a player steering with their right hand still reaches
 * every action without moving.
 *
 * @param {object} player  The player being steered (x, y, vx, vy, onBoard…).
 * @param {{x: number, y: number}} pointer Cursor position in canvas pixels.
 * @param {{boardSpeed: number, climbSpeed: number, playerSpeed: number}} limits
 *   Speed caps. climbSpeed bounds the hover's vertical axis, which is slower
 *   than its skating speed; it falls back to boardSpeed if not supplied.
 * @param {{jumpSpent?: boolean}} [memory] The one thing steering has to
 *   remember between frames — whether the current raise of the cursor has
 *   already been answered with a jump (see below). Owned by the caller and
 *   updated in place; everything else here is a pure function of its inputs.
 * @returns {{left: boolean, right: boolean, jump: boolean, down: boolean}}
 */
export function steerToPointer(player, pointer, limits, memory = {}) {
  // Hovering on the board is the only state with free vertical movement; on
  // foot, and while holding the board, the rider is on the ground under gravity.
  const hovering = player.onBoard && player.boardMode === 'feet';
  const maxX = hovering ? limits.boardSpeed : limits.playerSpeed;

  const ax = axisInput(pointer.x - player.x, player.vx, maxX);
  const frame = { left: ax < 0, right: ax > 0, jump: false, down: false };

  if (hovering) {
    // Hovering, the cursor's height is a place to fly to, not a jump request.
    // Stays spent, so the cursor left up here when the rider comes off the
    // board is not read as one the moment they land.
    memory.jumpSpent = true;
    const targetY = pointer.y + BODY_CENTER_OFFSET;
    // The vertical cap is the climb speed, not the skating speed. Aiming the
    // controller at a speed the keyboard cannot reach would have it press
    // down for longer than the physics can answer, and overshoot the cursor.
    const maxY = limits.climbSpeed ?? limits.boardSpeed;
    const ay = axisInput(targetY - player.y, player.vy, maxY);
    frame.jump = ay < 0; // up
    frame.down = ay > 0;
  } else {
    // The only vertical control left is a jump, and it only exists from the
    // ground — so the cursor has to be clearly overhead to ask for one. Reads
    // the flag the physics sets rather than testing for near-zero vertical
    // speed, which is also true at the apex of a jump already in progress.
    //
    // RAISING the cursor over the head is the request, not the cursor being
    // there. Read as a state, a cursor left overhead jumped the rider again on
    // every landing — and after coming off the board the cursor is always
    // overhead, because that is where they were hovering, so they bounced on
    // the spot until the player moved the mouse. One jump per raise: the raise
    // is spent by the jump it asks for, and re-armed only by lowering the
    // cursor again while standing — re-arming in the air would count the
    // rider rising past a still cursor as the cursor coming down.
    const grounded = !!player.grounded;
    const overhead = pointer.y < player.y - JUMP_REACH;
    if (grounded && !overhead) memory.jumpSpent = false;
    frame.jump = grounded && overhead && !memory.jumpSpent;
    if (frame.jump) memory.jumpSpent = true;
  }

  return frame;
}
